import { Controller } from '@hotwired/stimulus'
import { post } from '@rails/request.js'
import Sortable from 'sortablejs/modular/sortable.complete.esm.js'
import { DragScroll } from '../utils/drag_scroll'
import { DRAGGED } from '../utils/grid_items'

export default class extends Controller {
  static targets = ['container', 'item', 'button']
  static values = {
    changedUrl: String,
    autoActivate: Boolean,
  }

  #sortableAttribute = 'data-sortable'
  #resetForCache = () => this.#clearSortableAttributes()
  // The organize-mode pulses (cards, Terminer) share one clock: a CSS animation starts over
  // whenever its element is moved in the page — every swap, every page loaded meanwhile —
  // and drifted out of step with the others. Each start is set back on the page's timeline.
  #alignPulse = ({ target, animationName }) => {
    if (!PULSES.includes(animationName)) return

    for (const animation of target.getAnimations()) {
      if (animation.animationName === animationName) animation.startTime = 0
    }
  }

  connect() {
    document.addEventListener('turbo:before-cache', this.#resetForCache)
    document.addEventListener('animationstart', this.#alignPulse, true)
    // The server decides, through ?organize=true or the window after a native grouping. The order
    // is consumed on arrival: left in the markup, it went into Turbo's snapshot of the page, and a
    // restoration visit (back from a product, after Terminer) re-entered the mode on connect. The
    // markup then carries nothing of the mode into a cache (see #resetForCache too).
    if (this.autoActivateValue) {
      this.autoActivateValue = false
      this.activate()
    }
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.#resetForCache)
    document.removeEventListener('animationstart', this.#alignPulse, true)
    this.dragScroll?.detach()
    this.sortable?.destroy()
    this.sortable = undefined
  }

  activate(event) {
    if (this.sortable) return

    // The Organiser button is a link to ?organize=true: it is the no-JS fallback, and the only way
    // back to the default sort when an auto-sort is active (auto-sorts are not reorderable, and the
    // server skips sections in that case).
    // Under the default sort the page already is exactly what organize mode needs — sortable
    // targets and sections included — so we short-circuit the link and activate in place.
    if (this.#hasActiveSort) return

    event?.preventDefault()
    this.#init()
    this.buttonTargets.forEach((btn) => btn.setAttribute(this.#sortableAttribute, 'true'))
    // Notifies the native toolbar toggle (bridge--list-actions), among any listener.
    this.dispatch('activated')
  }

  deactivate(_event) {
    if (!this.sortable) return

    this.dragScroll?.detach()
    this.sortable.destroy()
    this.sortable = undefined
    this.#clearSortableAttributes()
    this.#stripOrganizeParam()
    this.dispatch('deactivated')
  }

  // Turbo caches a clone of the body on navigation, and organize mode is transient UI state. Drop
  // the attributes before it does: a snapshot cached mid-organize is painted as an instant preview
  // on the next visit, and this controller would connect onto it and re-enter a mode the user never
  // asked for — on a page the server rendered plain.
  #clearSortableAttributes() {
    this.#container.removeAttribute(this.#sortableAttribute)
    this.buttonTargets.forEach((btn) => btn.removeAttribute(this.#sortableAttribute))
  }

  // The Organiser link really navigates when a sort is active, so the param can be in the URL.
  // Leaving the mode should not leave it behind, or a refresh would drop the user back into it.
  // Nothing writes the param from here: the DOM is what Turbo caches, and a snapshot taken while
  // organizing would otherwise come back and re-add it on top of a page that is no longer in the
  // mode.
  #stripOrganizeParam() {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('organize')) return

    url.searchParams.delete('organize')
    // Pass history.state through: Turbo keeps its restorationIdentifier in there, and replacing it
    // with {} leaves Turbo unable to restore this entry.
    history.replaceState(history.state, '', url)
  }

  #init() {
    this.dragScroll = new DragScroll()
    this.dragScroll.attach()
    this.sortable = Sortable.create(this.#container, {
      sort: true,
      animation: 200,
      // Au toucher, la prise exige un court maintien : un doigt qui part
      // tout de suite défile au lieu de saisir la carte. Souris inchangée.
      delay: 75,
      delayOnTouchOnly: true,
      touchStartThreshold: 25,
      draggable: '[data-sortable-target="item"]',
      handle: '[data-sortable-handle]',
      // Un clic sur un élément interactif d'une poignée (croix de suppression des
      // tags gods) glissé d'un pixel partait en drag et avalait le clic — plus de
      // confirmation. preventOnFilter false : le clic filtré reste un vrai clic.
      filter: 'button, input, form',
      preventOnFilter: false,
      // SortableJS guesses the axis by sampling the first children; a full-width
      // section header up top makes it conclude "vertical", and card hovers are
      // then decided on Y alone: coming from above you are always "before" the
      // card you aim at, so the drop lands at the start of the row unless you
      // zigzag. Axis forced instead: horizontal between cards (X is what puts you
      // before or after in a row), vertical as soon as a full-width header is
      // involved — carried or hovered.
      direction: (_event, target, dragged) =>
        this.#isSection(dragged) || this.#isSection(target) ? 'vertical' : 'horizontal',
      // Swap only once the pointer reaches the middle half of the card aimed at: brushing
      // its edges on the way, a mouse swapped with every card it went over.
      swapThreshold: 0.5,
      // The copy following the cursor comes from SortableJS (a DOM clone, as on
      // touch) rather than from the browser: the native drag image carries an
      // unstylable system shadow, discreet behind a card but plainly visible
      // behind a transparent header line. Bonus: drag now renders identically on
      // desktop and mobile.
      forceFallback: true,
      // Marks the dragged item and that copy for the grid, which must leave both alone (DRAGGED).
      chosenClass: DRAGGED,
      // The page scrolls by DragScroll: near an edge, smoothly, and with a second finger.
      scroll: false,
      // The POST waits for the drop to finish (onEnd): for a section, the final
      // order only exists once its folded block has been put back behind it.
      onUpdate: () => (this.pendingReorder = true),
      // Not filed while the page scrolls by itself, nor twice under a still finger (DragScroll).
      onMove: (event) => {
        const allowed = !this.dragScroll.scrollingByHand && this.dragScroll.allowsSwap(event.related) &&
          this.#allowedMove(event)
        if (allowed) this.lastRelated = event.related
        return allowed
      },
      onChange: () => {
        this.dragScroll.markSwap(this.lastRelated)
        this.dispatch('changed')
      },
      onStart: (event) => {
        this.#foldSections(event.item)
        this.dragScroll.start()
      },
      onEnd: (event) => {
        this.dragScroll.stop()
        // Unfolding gives the page its full height back: we pin the dropped
        // section where it is, or the view jumps on release. Smoothly — the finger
        // has left the screen, and a hard snap would feel aggressive there.
        const restoreScroll = this.#isSection(event.item)
          ? this.#anchorScroll(event.item, { smooth: true })
          : null

        this.#unfoldSections()
        if (this.pendingReorder) {
          this.pendingReorder = false
          this.#reorder()
        }
        this.dispatch('ended')
        restoreScroll?.()
      },
    })
    this.#container.setAttribute(this.#sortableAttribute, 'true')
  }

  // While a section is being dragged, the only cards left visible are the UNGROUPED
  // ones (those above the first header — see #foldSections). Slotting in above them
  // would capture the whole vrac: a header may only be placed relative to another
  // header. Products go wherever they like.
  // "Autres envies" (data-sortable-pinned) holds one end of the list: no section crosses it. And no
  // card goes above the header that starts a list with sections, "Autres envies" or another — it
  // would land outside any section.
  #allowedMove({ dragged, related, willInsertAfter }) {
    if (this.#isSection(dragged)) {
      return this.#isSection(related) && !related.hasAttribute('data-sortable-pinned')
    }

    return !(this.#isSection(related) && !willInsertAfter && related === this.itemTargets[0])
  }

  // A section moves as a BLOCK. On lift-off we fold EVERY block, not just the one
  // being carried: the grid shrinks to the vertical list of its headers, where
  // moving a block is a plain up-and-down drag again — every position there is
  // valid by construction, whereas among cards a full-width header has almost no
  // legal landing spot. On release each block rejoins its header. Dragging a lone
  // product leaves all of this alone.
  #foldSections(item) {
    this.blocks = []
    if (!this.#isSection(item)) return

    // Hiding every card shrinks the page at once: without compensation a low
    // scroll position ends up past the new bottom — blank screen, unmanageable
    // drag, especially on mobile. We keep the carried header under the finger.
    const restoreScroll = this.#anchorScroll(item)
    let block = null
    for (const child of this.#container.children) {
      if (this.#isSection(child)) {
        block = { header: child, cards: [] }
        this.blocks.push(block)
      } else if (block && child.id?.startsWith('product_')) {
        block.cards.push(child)
        child.style.display = 'none'
      }
      // Products above the first header (the vrac) stay visible: they belong to no
      // block and keep their place up top.
    }

    // The header travels alone: the badge says how many products follow it.
    this.#showFoldBadge(this.blocks.find((candidate) => candidate.header === item))
    // The masonry grid recomputes its columns on the fold (no-op in grid mode).
    this.dispatch('folded')
    restoreScroll()
  }

  // Freezes an element's on-screen position across an upheaval of the page height.
  // Returns the compensation to call once the mutations are requested: it applies on
  // the next frame, after the masonry relayout (itself in a requestAnimationFrame),
  // when positions have settled. Hard snap during the drag (it must stick to the
  // finger), animated on release.
  #anchorScroll(element, { smooth = false } = {}) {
    const top = element.getBoundingClientRect().top

    return () => {
      window.requestAnimationFrame(() => {
        window.scrollBy({
          top: element.getBoundingClientRect().top - top,
          behavior: smooth && !this.#prefersReducedMotion ? 'smooth' : 'auto',
        })
      })
    }
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  #showFoldBadge(block) {
    const row = block?.header.firstElementChild
    if (!row || !block.cards.length) return

    this.foldBadge = document.createElement('span')
    this.foldBadge.className =
      'shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-sm font-medium text-gray-600'
    this.foldBadge.textContent =
      block.cards.length === 1 ? '1 envie' : `${block.cards.length} envies`
    // Between the title and the rule of actions.
    row.insertBefore(this.foldBadge, row.lastElementChild)
  }

  #unfoldSections() {
    this.foldBadge?.remove()
    this.foldBadge = null

    this.blocks?.forEach(({ header, cards }) => {
      let previous = header
      cards.forEach((card) => {
        previous.after(card)
        card.style.display = ''
        previous = card
      })
    })
    this.blocks = []
  }

  #isSection(element) {
    return Boolean(element?.id?.startsWith('list_section_'))
  }

  #reorder() {
    post(this.changedUrlValue, {
      responseKind: 'turbo-stream',
      body: { item_ids: this.#itemIds() },
    })
  }

  #itemIds() {
    return this.itemTargets.map((e) => e.id)
  }

  // Read from the URL at click time rather than frozen into a value: sort links navigate the
  // `products` frame with turbo_action="replace", so the URL tracks the current sort while this
  // controller stays connected.
  get #hasActiveSort() {
    return new URL(window.location.href).searchParams.has('sort')
  }

  get #container() {
    return this.hasContainerTarget ? this.containerTarget : this.element
  }
}

const PULSES = ['darken-pulse', 'brightness-pulse', 'done-pulse']
